import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator, Platform } from 'react-native';
import { Link, useLocalSearchParams, usePathname } from 'expo-router';
import { useState, useEffect } from 'react';
import { MaterialIcons } from '@expo/vector-icons';
import { supabase, toError } from '../../../../lib/supabase';
import { getDigestUrl } from '../../../../lib/paperUrl';
import Header from '../../../../components/Header';
import Footer from '../../../../components/Footer';
import MetaTags from '../../../../components/MetaTags';
import { PaperFeedButton, ProjectSettingsButton } from '../../../../components/ui/Button';
import colors from '../../../../lib/colors';
import { useFontLoading } from '../../../../lib/FontContext';
import { fonts, interSemiBold, interMedium, interRegular } from "../../../../lib/fonts";
import { useSession } from '../../../../lib/SessionContext';
import { canAccessMainApp } from '../../../../utils/authHelpers';
import AppRouteBootScreen from '../../../../components/auth/AppRouteBootScreen';
import FullPageSignInGate from '../../../../components/auth/FullPageSignInGate';
import { getRouteModeRedirect } from '../../../../lib/routeMode';
import { useAuth } from '../../../../lib/AuthContext';
import PrivateDigestAccessCard from '../../../../components/digest/PrivateDigestAccessCard';
import { PRIVATE_OR_NOT_FOUND, isPrivateOrNotFoundError, toPrivateOrNotFoundError } from '../../../../lib/accessErrors';
import { buildCanonicalDashboardPath, buildDashboardPath } from '../../../../lib/dashboardRoutes';
import { Plural, Trans } from '@lingui/react/macro';
import { msg, plural } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { useAppLocale } from '../../../../lib/i18n/AppI18nProvider';
import { useOptionalResolvedMessage, type LocalizedMessage } from '../../../../lib/i18n/messages';

interface DigestIssue {
    id: string;
    short_id?: string;
    created_at: string;
    sent_at: string | null;
    status: string | null;
    email_sent: boolean;
    digest_papers: { count: number }[];
}

/**
 * DigestIssuesPage displays a list of all sent digest issues for a project
 * Route: /digests/[digestId]/issues
 * Access: Protected (requires authentication, user must own digest)
 */
export default function DigestIssuesPage() {
    const { i18n } = useLingui();
    const { formatLocale } = useAppLocale();
    const { digestId } = useLocalSearchParams<{ digestId: string }>();
    const pathname = usePathname() || `/digests/${digestId || ''}/issues`;
    const { fontsLoaded } = useFontLoading();
    const { session, isLoading: sessionLoading } = useSession();
    const { openAuth } = useAuth();
    const hasMainAppAccess = canAccessMainApp(session);
    const redirectTo = getRouteModeRedirect(pathname);
    const projectPath = buildDashboardPath(pathname, `/${digestId}`);
    const projectSettingsPath = buildDashboardPath(pathname, `/${digestId}/settings`);

    const [issues, setIssues] = useState<DigestIssue[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<LocalizedMessage | null>(null);
    const resolvedError = useOptionalResolvedMessage(error);
    const [digestName, setDigestName] = useState<string>('');

    useEffect(() => {
        if (digestId && !sessionLoading && hasMainAppAccess) {
            fetchIssues();
        }
    }, [digestId, sessionLoading, hasMainAppAccess]);

    const fetchIssues = async () => {
        setLoading(true);
        setError(null);

        try {
            console.log('📋 [DigestIssues] Fetching issues for project:', digestId);

            // Fetch project name first
            const { data: projectData, error: projectError } = await supabase
                .from('project')
                .select('name')
                .eq('id', digestId)
                .is('deleted_at', null)
                .single();

            if (projectError) {
                console.error('📋 [DigestIssues] Error fetching project:', projectError);
                const accessError = toPrivateOrNotFoundError(projectError);
                if (accessError) {
                    throw accessError;
                }
                throw toError(projectError);
            }

            setDigestName(projectData.name);

            // Fetch all digest issues for this project
            const { data, error: fetchError } = await supabase
                .from('email_digest')
                .select(`
                    id,
                    short_id,
                    created_at,
                    sent_at,
                    status,
                    email_sent,
                    digest_papers(count)
                `)
                .eq('project_id', digestId)
                .order('created_at', { ascending: false });

            if (fetchError) {
                console.error('📋 [DigestIssues] Error fetching issues:', fetchError);
                const accessError = toPrivateOrNotFoundError(fetchError);
                if (accessError) {
                    throw accessError;
                }
                throw toError(fetchError);
            }

            console.log('📋 [DigestIssues] Fetched', data?.length || 0, 'issues');
            setIssues((data || []) as DigestIssue[]);
        } catch (err: any) {
            console.error('📋 [DigestIssues] Unexpected error:', err);
            setError(isPrivateOrNotFoundError(err)
                ? PRIVATE_OR_NOT_FOUND
                : msg({
                    id: 'digestIssues.loadFailed',
                    message: 'Failed to load digest issues',
                    comment: 'Generic digest issue loading error shown to the customer; raw errors remain in logs.',
                }));
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now.getTime() - date.getTime());
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return i18n._(msg({ id: 'common.date.today', message: 'Today' }));
        if (diffDays === 1) return i18n._(msg({ id: 'common.date.yesterday', message: 'Yesterday' }));
        if (diffDays < 7) return i18n._(msg({ id: 'common.date.daysAgo', message: `${diffDays} days ago` }));
        if (diffDays < 30) {
            const weeks = Math.floor(diffDays / 7);
            return i18n._(msg({ id: 'common.date.weeksAgo', message: plural(weeks, { one: '# week ago', other: '# weeks ago' }) }));
        }

        return date.toLocaleDateString(formatLocale, {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getStatusBadge = (status: string | null, emailSent: boolean) => {
        // Determine display based on actual database status
        // Database values: 'pending', 'sending', 'sent', 'failed'
        let displayStatus: string;
        let badgeColor: string;
        let badgeBackground: string;

        switch (status) {
            case 'pending':
                displayStatus = i18n._(msg({ id: 'digest.status.pending', message: 'Pending' }));
                badgeColor = colors.text500;
                badgeBackground = colors.bgWeak;
                break;
            case 'sending':
                displayStatus = i18n._(msg({ id: 'digest.status.sending', message: 'Sending' }));
                badgeColor = '#d97706';
                badgeBackground = '#fef3c7';
                break;
            case 'sent':
                if (!emailSent) {
                    displayStatus = i18n._(msg({ id: 'digest.status.appOnly', message: 'App only' }));
                    badgeColor = colors.text500;
                    badgeBackground = colors.bgWeak;
                } else {
                    displayStatus = i18n._(msg({ id: 'digest.status.sent', message: 'Sent' }));
                    badgeColor = colors.primaryGreen;
                    badgeBackground = colors.primary100;
                }
                break;
            case 'failed':
                displayStatus = i18n._(msg({ id: 'digest.status.failed', message: 'Failed' }));
                badgeColor = colors.errorRed;
                badgeBackground = '#fee2e2';
                break;
            default:
                displayStatus = i18n._(msg({ id: 'common.status.unknown', message: 'Unknown' }));
                badgeColor = colors.text500;
                badgeBackground = colors.bgWeak;
                break;
        }

        return (
            <View style={[styles.statusBadge, { backgroundColor: badgeBackground }]}>
                <Text style={[styles.statusText, { color: badgeColor }, fontsLoaded && interSemiBold]}>
                    {displayStatus}
                </Text>
            </View>
        );
    };

    if (sessionLoading) {
        return <AppRouteBootScreen message={msg({ id: "digests.byDigestid.issues.loadingDigestIssues", message: "Loading digest issues...", comment: "UI message in app/(app)/digests/[digestId]/issues.tsx" })} />;
    }

    if (!hasMainAppAccess) {
        return (
            <FullPageSignInGate
                redirectTo={redirectTo}
                title={msg({ id: "digests.byDigestid.issues.signInToViewDigestIssues", message: "Sign in to view digest issues", comment: "UI title in app/(app)/digests/[digestId]/issues.tsx" })}
                message={msg({ id: "digests.byDigestid.issues.thisDigestIsPrivateToYourAccount", message: "This digest is private to your account.", comment: "UI message in app/(app)/digests/[digestId]/issues.tsx" })}
                primaryActionLabel={msg({ id: "digests.byDigestid.issues.continue", message: "Continue", comment: "UI primaryActionLabel in app/(app)/digests/[digestId]/issues.tsx" })}
            />
        );
    }

    // Loading state
    if (loading) {
        return (
            <View style={styles.container}>
                <Header />
                <View style={styles.loadingContainer}>
                    <ActivityIndicator size="large" color={colors.primaryGreen} />
                    <Text style={[styles.loadingText, fontsLoaded && interRegular]}><Trans id="digests.byDigestid.issues.loadingDigestIssues" comment="UI copy in app/(app)/digests/[digestId]/issues.tsx">
                        Loading digest issues...
                    </Trans></Text>
                </View>
                <Footer />
            </View>
        );
    }

    // Error state
    if (error) {
        if (isPrivateOrNotFoundError(error)) {
            return (
                <View style={styles.container}>
                    <Header />
                    <ScrollView
                        style={styles.scrollView}
                        contentContainerStyle={styles.privateScrollContent}
                        showsVerticalScrollIndicator={false}
                    >
                        <View style={styles.privateContentWrapper}>
                            <PrivateDigestAccessCard
                                isAuthenticated={hasMainAppAccess}
                                onSignIn={() => openAuth({ redirectTo })}
                                onSwitchAccount={() => openAuth({ redirectTo, forSwitchAccount: true })}
                                fontsLoaded={fontsLoaded}
                            />
                        </View>
                        <Footer />
                    </ScrollView>
                </View>
            );
        }

        return (
            <View style={styles.container}>
                <Header />
                <View style={styles.errorContainer}>
                    <Text style={[styles.errorTitle, fontsLoaded && interSemiBold]}><Trans id="digests.byDigestid.issues.errorLoadingIssues" comment="UI copy in app/(app)/digests/[digestId]/issues.tsx">
                        Error Loading Issues
                    </Trans></Text>
                    <Text style={[styles.errorText, fontsLoaded && interRegular]}>
                        {resolvedError}
                    </Text>
                </View>
                <Footer />
            </View>
        );
    }

    // Success state - render issues list
    return (
        <>
            <MetaTags
                title={`${digestName} - Paper Digests - Paperzilla`}
                description="View all paper digests"
                url={`https://paperzilla.ai${buildCanonicalDashboardPath(`/${digestId}/issues`)}`}
            />

            <View style={styles.container}>
                <Header />
                <ScrollView
                    style={styles.scrollView}
                    contentContainerStyle={styles.scrollContent}
                    showsVerticalScrollIndicator={false}
                >
                    <View style={styles.contentWrapper}>
                        <View style={styles.content}>
                            <View style={styles.headerRow}>
                                <View style={styles.headerTextBlock}>
                                    <Text style={[styles.title, fontsLoaded && interSemiBold]}><Trans id="digests.byDigestid.issues.paperDigests" comment="UI copy in app/(app)/digests/[digestId]/issues.tsx">
                                        Paper Digests
                                    </Trans></Text>

                                    <Text style={[styles.description, fontsLoaded && interRegular]}><Trans id="digests.byDigestid.issues.browseAllPaperDigestsFor" comment="UI copy in app/(app)/digests/[digestId]/issues.tsx">
                                        Browse all paper digests for {digestName}
                                    </Trans></Text>
                                </View>

                                <View style={styles.actionRow}>
                                    <Link href={projectSettingsPath} asChild>
                                        <ProjectSettingsButton
                                            variant="secondary"
                                            style={styles.actionButton}
                                            textStyle={styles.actionButtonText}
                                            iconSize={14}
                                            testID="digest-issues-settings-button"
                                        />
                                    </Link>
                                    <Link href={projectPath} asChild>
                                        <PaperFeedButton
                                            variant="secondary"
                                            style={styles.actionButton}
                                            textStyle={styles.actionButtonText}
                                            iconSize={14}
                                            testID="digest-issues-feed-button"
                                        />
                                    </Link>
                                </View>
                            </View>

                            {/* Issues list */}
                            {issues.length === 0 ? (
                                <View style={styles.emptyState}>
                                    <MaterialIcons name="inbox" size={64} color={colors.text500} />
                                    <Text style={[styles.emptyTitle, fontsLoaded && interSemiBold]}><Trans id="digests.byDigestid.issues.noIssuesYet" comment="UI copy in app/(app)/digests/[digestId]/issues.tsx">
                                        No issues yet
                                    </Trans></Text>
                                    <Text style={[styles.emptyText, fontsLoaded && interRegular]}><Trans id="digests.byDigestid.issues.digestIssuesWillAppearHereOnceTheyVeBeenSent" comment="UI copy in app/(app)/digests/[digestId]/issues.tsx">
                                        Digest issues will appear here once they've been sent.
                                    </Trans></Text>
                                </View>
                            ) : (
                                <View style={styles.issuesGrid}>
                                    {issues.map((issue) => (
                                        <Link
                                            key={issue.id}
                                            href={getDigestUrl(issue.short_id || issue.id, digestName)}
                                            asChild
                                        >
                                            <TouchableOpacity style={styles.issueCard} activeOpacity={0.7}>
                                                {/* Status badge */}
                                                {getStatusBadge(issue.status, issue.email_sent)}

                                                {/* Date */}
                                                <Text style={[styles.issueDate, fontsLoaded && interSemiBold]}>
                                                    {formatDate(issue.created_at)}
                                                </Text>

                                                {/* Paper count */}
                                                <View style={styles.issueMeta}>
                                                    <MaterialIcons name="article" size={16} color={colors.text700} />
                                                    <Text style={[styles.issueMetaText, fontsLoaded && interRegular]}><Plural
                                                        id="digests.byDigestid.issues.papers"
                                                        comment="Number of scientific papers in this digest issue"
                                                        value={issue.digest_papers?.[0]?.count || 0}
                                                        one="# paper"
                                                        other="# papers"
                                                    /></Text>
                                                </View>

                                                {/* Arrow icon */}
                                                <View style={styles.arrowContainer}>
                                                    <MaterialIcons name="arrow-forward" size={20} color={colors.primaryGreen} />
                                                </View>
                                            </TouchableOpacity>
                                        </Link>
                                    ))}
                                </View>
                            )}
                        </View>
                    </View>
                    <Footer />
                </ScrollView>
            </View>
        </>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: colors.bgWeak,
    },
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        flexGrow: 1,
    },
    contentWrapper: {
        flex: 1,
        alignItems: 'center',
    },
    content: {
        width: '100%',
        maxWidth: 1200,
        paddingHorizontal: 20,
        paddingTop: 20,
        paddingBottom: 40,
        ...(Platform.OS === 'web' && {
            alignSelf: 'center',
        }),
    },
    title: {
        fontSize: 32,
        fontWeight: '700',
        color: colors.text900,
        marginBottom: 8,
    },
    headerRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 16,
        marginBottom: 32,
        flexWrap: 'wrap',
    },
    headerTextBlock: {
        flex: 1,
        minWidth: 260,
    },
    description: {
        fontSize: 16,
        color: colors.text700,
        lineHeight: 24,
    },
    actionRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        flexShrink: 1,
        maxWidth: '100%',
        gap: 12,
    },
    actionButton: {
        maxWidth: '100%',
        minHeight: 44,
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderRadius: 10,
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
        }),
    },
    actionButtonText: {
        flexShrink: 1,
        fontSize: 14,
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingVertical: 60,
    },
    loadingText: {
        marginTop: 16,
        fontSize: 16,
        color: colors.text700,
    },
    errorContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 40,
        paddingVertical: 60,
    },
    errorTitle: {
        fontSize: 24,
        fontWeight: '600',
        color: colors.text900,
        marginBottom: 12,
        textAlign: 'center',
    },
    errorText: {
        fontSize: 16,
        color: colors.text700,
        textAlign: 'center',
        lineHeight: 24,
    },
    privateScrollContent: {
        flexGrow: 1,
        paddingVertical: 48,
    },
    privateContentWrapper: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 20,
    },
    emptyState: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 80,
        gap: 16,
    },
    emptyTitle: {
        fontSize: 20,
        fontWeight: '600',
        color: colors.text900,
    },
    emptyText: {
        fontSize: 16,
        color: colors.text700,
        textAlign: 'center',
        lineHeight: 24,
        maxWidth: 400,
    },
    issuesGrid: {
        gap: 16,
    },
    issueCard: {
        backgroundColor: colors.card,
        borderRadius: 12,
        padding: 20,
        borderWidth: 1,
        borderColor: colors.border,
        position: 'relative',
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
            transition: 'all 0.2s',
        }),
    },
    statusBadge: {
        position: 'absolute',
        top: 12,
        right: 12,
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 12,
    },
    statusText: {
        fontSize: 12,
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    issueDate: {
        fontSize: 18,
        fontWeight: '600',
        color: colors.text900,
        marginBottom: 12,
        paddingRight: 80, // Space for status badge
    },
    issueMeta: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    issueMetaText: {
        fontSize: 14,
        color: colors.text700,
    },
    arrowContainer: {
        position: 'absolute',
        bottom: 20,
        right: 20,
    },
});
